import type { PayRule } from "./wire-types";
import { minorPerMajor, money } from "./format";

// SWEEP S6. The pure half of the configuration editor (screens/ConfigurationEdit.tsx):
// keys the database accepts, money in minor units, and the rate presets with the spec
// each one sends. Kept apart so test/configuration-edit.test.ts can hold it to the
// database's key check and the server's required spec keys.

/** A key the database accepts: `^[a-z][a-z0-9_]{1,58}[a-z0-9]$`. */
export function keyFrom(name: string): string {
  const k = name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^[^a-z]+/, "")
    .replace(/_+$/, "")
    .slice(0, 60);
  return k.length >= 3 ? k : `${k || "item"}_x`.slice(0, 60);
}

/**
 * Minor units from what a person types, in the currency's own ISO 4217 exponent — "2 500"
 * forints is 250000 fillér, as the database stores it, and "2 500,50" pounds is 250050.
 * (Until 21 October 2026 this said 2500: it trusted Intl, whose forint changed to its cash
 * digits, 0. See `minorPerMajor`.)
 */
export function minorFrom(text: string, currency: string): number | null {
  const n = Number(text.replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? Math.round(n * minorPerMajor(currency)) : null;
}

type Preset = {
  id: string;
  label: string;
  hint: string;
  fields: readonly { key: string; label: string; placeholder: string }[];
  build: (v: Record<string, string>, zone: string, currency: string) => Record<string, unknown> | string;
};

const WEEKDAYS = ["1", "2", "3", "4", "5", "6", "7"];

export const PRESETS: readonly Preset[] = [
  {
    id: "base",
    label: "Hourly rate",
    hint: "What every hour on this pay rule set is paid, before anything below.",
    fields: [{ key: "rate", label: "Per hour", placeholder: "2500" }],
    build: (v, _zone, currency) => {
      const m = minorFrom(v.rate ?? "", currency);
      return m === null || m === 0 ? "Give the hourly rate." : { effect_kind: "rate_override", effect_spec: { rate_minor: m } };
    },
  },
  {
    id: "minimum",
    label: "Minimum paid hours",
    hint: "A short shift is paid as at least this many hours.",
    fields: [{ key: "hours", label: "Hours", placeholder: "4" }],
    build: (v) => {
      const h = Number(v.hours);
      return h > 0 ? { effect_kind: "minimum_hours", effect_spec: { hours: h } } : "Give the minimum hours.";
    },
  },
  {
    id: "night",
    label: "Night uplift",
    hint: "Hours between two clock times are paid at a multiple of the hourly rate.",
    fields: [
      { key: "from", label: "From", placeholder: "22:00" },
      { key: "to", label: "To", placeholder: "06:00" },
      { key: "value", label: "Multiplier", placeholder: "1.25" },
    ],
    build: (v, zone) => {
      const value = Number(v.value);
      if (!/^\d{2}:\d{2}$/.test(v.from ?? "") || !/^\d{2}:\d{2}$/.test(v.to ?? "")) return "Give both times as HH:MM.";
      if (!(value > 1)) return "A multiplier above 1, such as 1.25.";
      return {
        effect_kind: "multiplier",
        effect_spec: { value },
        trigger_kind: "clock_between",
        trigger_spec: { from: v.from, to: v.to, timezone: zone },
      };
    },
  },
  {
    id: "weekend",
    label: "Weekend uplift",
    hint: "Hours on Saturday and Sunday are paid at a multiple of the hourly rate.",
    fields: [{ key: "value", label: "Multiplier", placeholder: "1.5" }],
    build: (v, zone) => {
      const value = Number(v.value);
      return value > 1
        ? {
            effect_kind: "multiplier",
            effect_spec: { value },
            trigger_kind: "weekday_in",
            trigger_spec: { weekdays: WEEKDAYS.slice(5), timezone: zone },
          }
        : "A multiplier above 1, such as 1.5.";
    },
  },
  {
    id: "overtime",
    label: "Overtime",
    hint: "Hours beyond a daily threshold are paid at a multiple of the hourly rate.",
    fields: [
      { key: "hours", label: "After hours in a day", placeholder: "8" },
      { key: "value", label: "Multiplier", placeholder: "1.5" },
    ],
    build: (v) => {
      const hours = Number(v.hours);
      const value = Number(v.value);
      if (!(hours > 0)) return "Give the daily threshold in hours.";
      if (!(value > 1)) return "A multiplier above 1, such as 1.5.";
      return {
        effect_kind: "overtime_multiplier",
        effect_spec: { value },
        trigger_kind: "hours_over",
        trigger_spec: { hours, scope: "daily" },
      };
    },
  },
  {
    id: "fixed",
    label: "Fixed addition per shift",
    hint: "A set amount added to every shift — a travel or kit allowance.",
    fields: [{ key: "amount", label: "Amount", placeholder: "3000" }],
    build: (v, _zone, currency) => {
      const m = minorFrom(v.amount ?? "", currency);
      return m === null || m === 0 ? "Give the amount." : { effect_kind: "add_fixed", effect_spec: { amount_minor: m } };
    },
  },
];

/**
 * One line a person can read back, from the spec the engine will use. S70 (0204): the
 * words are each kind's own phrase from the database, and `{key}` is filled from the
 * spec — money when the key ends `_minor`. No kind is compared here.
 */
export function describeRule(r: PayRule, currency: string): string {
  const fill = (phrase: string, spec: unknown) =>
    phrase.replace(/\{([a-z_]+)\}/g, (_, k: string) => {
      const v = (spec as Record<string, unknown> | null)?.[k];
      if (k.endsWith("_minor")) return money(Number(v), currency);
      return Array.isArray(v) ? v.join(", ") : String(v);
    });
  const what = fill(r.effect_phrase, r.effect_spec);
  return r.trigger_phrase ? `${what} ${fill(r.trigger_phrase, r.trigger_spec)}` : what;
}
